import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { aiToast } from './shared/ai-toast';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import ModelSelect from './shared/model-select';
import { OptimizationRunsTable } from './shared/optimization-runs';
import {
  saveSettings,
  SettingsSaveBar,
  useSettingsForm,
} from './shared/settings-form';

type AiSettings = Record<string, string>;

function OptimizationCronTab() {
  const [settings, setSettings] = useState<AiSettings | null>(null);

  const { draft, setField, dirty, saving, setSaving, reset } =
    useSettingsForm(settings);

  const load = useCallback(async () => {
    const settingsData = await fetchWrapper.get(urls.DevOps.aiEngineSettings);
    setSettings(settingsData);
  }, []);

  useEffect(() => {
    load().catch(() => aiToast.error('Failed to load AI optimization settings'));
  }, [load]);

  const handleSave = async () => {
    if (!draft || !settings) return;
    setSaving(true);
    try {
      const updated = await saveSettings(
        urls.DevOps.aiEngineSettings,
        draft,
        settings,
      );
      setSettings(updated);
    } catch (error: any) {
      aiToast.error(error?.message ?? 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!settings || !draft) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Optimization — cron settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure the scheduled job that analyzes all active subscriptions on
          the cron below.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <SettingsSaveBar
          dirty={dirty}
          saving={saving}
          onSave={handleSave}
          onReset={reset}
          saveTestId="ai-save-button"
          resetTestId="ai-reset-button"
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={draft.enabled === 'true'}
              onChange={(e) =>
                setField('enabled', e.target.checked ? 'true' : 'false')
              }
              data-testid="ai-engine-enabled"
            />
          }
          label="Engine enabled (cron analyzes all active subscriptions)"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Schedule (cron, Europe/Warsaw)"
          value={draft.cron_expression ?? ''}
          onChange={(e) => setField('cron_expression', e.target.value)}
          data-testid="ai-engine-cron"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <ModelSelect
          value={draft.model ?? ''}
          onChange={(v) => setField('model', v)}
          testId="ai-engine-model"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Max findings per subscription"
          type="number"
          value={draft.max_findings_per_subscription ?? '20'}
          onChange={(e) =>
            setField('max_findings_per_subscription', e.target.value)
          }
          data-testid="ai-engine-max-findings"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Claude timeout (ms)"
          type="number"
          value={draft.claude_timeout_ms ?? '300000'}
          onChange={(e) => setField('claude_timeout_ms', e.target.value)}
          data-testid="ai-engine-timeout"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={6}
          label="Default analysis prompt"
          helperText="Used by the scheduled cron job."
          value={draft.base_prompt ?? ''}
          onChange={(e) => setField('base_prompt', e.target.value)}
          data-testid="ai-engine-prompt"
        />
      </Grid>

      <Grid item xs={12}>
        <OptimizationRunsTable
          triggerFilter="cron"
          title="Scheduled run history"
          testId="ai-cron-batch-runs"
        />
      </Grid>
    </Grid>
  );
}

export default OptimizationCronTab;
